import React from 'react';

/**
 * Animated TeenVest logo loader.
 * Three ascending bars (short → medium → tall) staircase upward one at a
 * time, followed by a diagonal upward arrow that draws itself through them.
 * The whole cycle loops until the caller unmounts the component.
 */
const TeenVestLoader = ({ size = 96, label = 'Loading...' }) => {
  const s = size;
  return (
    <div className="inline-flex flex-col items-center justify-center" role="status" aria-live="polite" data-testid="teenvest-loader">
      <style>{`
        @keyframes tv-bar-grow {
          0%   { transform: scaleY(0);   opacity: 0.35; }
          15%  { transform: scaleY(1);   opacity: 1;    }
          75%  { transform: scaleY(1);   opacity: 1;    }
          100% { transform: scaleY(0);   opacity: 0.35; }
        }
        @keyframes tv-arrow-draw {
          0%,  20% { stroke-dashoffset: 90; opacity: 0; }
          40%      { stroke-dashoffset: 90; opacity: 0.4; }
          65%      { stroke-dashoffset: 0;  opacity: 1;   }
          85%      { stroke-dashoffset: 0;  opacity: 1;   }
          100%     { stroke-dashoffset: 90; opacity: 0;   }
        }
        @keyframes tv-ring-pulse {
          0%, 100% { opacity: 0.35; transform: scale(1);   }
          50%      { opacity: 0.7;  transform: scale(1.03); }
        }
        .tv-bar   { transform-origin: bottom center; animation: tv-bar-grow 2.4s cubic-bezier(.22,.7,.18,1) infinite; }
        .tv-bar-1 { animation-delay: 0.00s; }
        .tv-bar-2 { animation-delay: 0.28s; }
        .tv-bar-3 { animation-delay: 0.56s; }
        .tv-arrow { stroke-dasharray: 90; animation: tv-arrow-draw 2.4s cubic-bezier(.22,.7,.18,1) infinite; }
        .tv-ring  { animation: tv-ring-pulse 2.4s ease-in-out infinite; transform-origin: center; }
      `}</style>

      <svg viewBox="0 0 100 100" width={s} height={s} aria-hidden="true">
        <defs>
          <linearGradient id="tvGold" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#FDE68A" />
            <stop offset="55%"  stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#8A6A0F" />
          </linearGradient>
        </defs>

        {/* Circular ring — the gold ring around the logo */}
        <circle
          className="tv-ring"
          cx="50" cy="50" r="42"
          fill="none"
          stroke="url(#tvGold)"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* Three ascending bars (short → medium → tall) */}
        <rect className="tv-bar tv-bar-1" x="26" y="46" width="10" height="26" rx="1.5" fill="url(#tvGold)" />
        <rect className="tv-bar tv-bar-2" x="42" y="34" width="10" height="38" rx="1.5" fill="url(#tvGold)" />
        <rect className="tv-bar tv-bar-3" x="58" y="22" width="10" height="50" rx="1.5" fill="url(#tvGold)" />

        {/* Diagonal up-right arrow drawing from bottom-left to top-right */}
        <g className="tv-arrow" stroke="url(#tvGold)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <line x1="24" y1="70" x2="76" y2="22" />
          <polyline points="60,20 78,20 78,38" />
        </g>
      </svg>

      {label && <p className="mt-3 text-sm text-zinc-400" data-testid="teenvest-loader-label">{label}</p>}
    </div>
  );
};

export default TeenVestLoader;
